import { ManualHearingForm } from "./pages/CaseDialogs";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, Mail, ListChecks, Inbox, FileStack, PenLine, Save } from "lucide-react";
import api from "../services/api";
import { ujmsFindSessions, ujmsScheduleHearing } from "../services/ujmsApi";
import { openPdfInViewer } from "./PdfViewer";
import { inputCls, selectCls, btnPrimary, btnGhost, cls, Seg } from "./ui";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "../components/ui/dialog";

const err = (e, f) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);

// ---------------- Select Document (case documents engine + envelopes) ----------------
export function SelectDocumentDialog({ open, onClose, caseId, caseNumber }) {
  const [name, setName] = useState("");
  const [types, setTypes] = useState([]);
  const [page, setPage] = useState(0);
  useEffect(() => {
    if (!open) return;
    setName(""); setPage(0);
    api.get("/ujms/v2/case-forms/meta").then((r) => setTypes(r.data.form_types || [])).catch(() => setTypes([]));
  }, [open]);
  const search = () => setPage(0);
  const rows = useMemo(() => {
    const ql = name.trim().toLowerCase();
    const specials = [];
    if (!ql || "envelope".includes(ql)) {
      specials.push({ key: "envelope", name: "Envelope", type: "Addressee Picker (Case Parties)", owner: "CJMS Automation",
        sub: "Merges party and counsel addresses from this case — prints directly on #10 envelopes",
        open: () => openPdfInViewer(`/ujms/cases/${caseId}/envelopes/pdf`, `Envelopes — ${caseNumber}`) });
    }
    return [...specials, ...types.filter((t) => !ql || `${t.code} ${t.label} ${t.help}`.toLowerCase().includes(ql)).map((t) => ({
      key: t.code, name: t.label, type: t.criminal_only ? "Criminal document" : "Case document", owner: "CJMS Documents", sub: t.help,
      open: () => openPdfInViewer(`/ujms/v2/cases/${caseId}/documents/${t.code}/preview`, `${t.label} — ${caseNumber}`) }))];
  }, [types, name, caseId, caseNumber]);
  const PAGE = 10;
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const view = rows.slice(page * PAGE, (page + 1) * PAGE);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0" data-testid="select-document-dialog">
        <DialogTitle className="sr-only">Select Document</DialogTitle>
        <div className="px-4 pt-3 pb-2">
          <div className="text-[15px] text-ujms-dark" data-testid="select-document-title">Select Document</div>
          <div className="flex items-center gap-3 mt-2">
            <label className="text-[12px] text-slate-600">Name</label>
            <input className={cls(inputCls, "w-56 h-7 text-[12px]")} value={name} autoFocus
              onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              data-testid="select-document-name" />
            <label className="text-[12px] text-slate-600 ml-3">Type</label>
            <select className={cls(selectCls, "w-24 h-7 text-[12px] py-0")} defaultValue="All"><option>All</option></select>
            <label className="ml-auto flex items-center gap-1 text-[12px] text-slate-600">
              <input type="checkbox" defaultChecked readOnly /> Print Preview
            </label>
          </div>
        </div>
        <div className="mx-4 border border-[#9db8d2]" data-testid="select-document-results">
          <div className="grid grid-cols-[44px_1fr_180px_130px] tyler-th text-[11px] px-2 py-1">
            <span>Info</span><span>Name</span><span>Type</span><span>Owner</span>
          </div>
          <div className="max-h-[320px] overflow-y-auto bg-white">
            {view.map((r, i) => (
              <button key={r.key} onClick={r.open} data-testid={`select-document-row-${r.key}`}
                className={cls("w-full text-left grid grid-cols-[44px_1fr_180px_130px] px-2 py-1 text-[12px] hover:bg-ujms-tint",
                  i % 2 ? "bg-ujms-soft" : "bg-white")}>
                <span>📩</span>
                <span><u className="text-slate-800">{page * PAGE + i + 1}.</u> <b>{r.name}</b>
                  <div className="text-[11px] text-slate-500 ml-4">🗎 {r.sub}</div></span>
                <span className="text-slate-600">{r.type}</span>
                <span className="text-slate-600">{r.owner}</span>
              </button>
            ))}
            {!view.length && <div className="p-4 text-[12px] text-slate-400">Type a document name (try "envelope") and press Search.</div>}
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-[12px] text-slate-600" data-testid="select-document-count">{rows.length} Results - Page {page + 1} of {pages}</span>
          <div className="flex gap-1.5">
            {[["<< Back", () => setPage((p) => Math.max(0, p - 1))], ["Next >>", () => setPage((p) => Math.min(pages - 1, p + 1))],
              ["Search", search], ["Exit", onClose]].map(([l, fn]) => (
              <button key={l} onClick={fn} data-testid={`select-document-${l.replace(/[^a-z]/gi, "").toLowerCase()}`}
                className="border border-[#8ba7c2] bg-gradient-to-b from-white to-[#dde8f2] text-[12px] px-4 py-1 rounded-[2px] hover:from-[#eaf2fa] text-slate-700">
                {l}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Select Calendar (classic picker) ----------------
function SelectCalendarDialog({ open, onClose, onSelect }) {
  const [cals, setCals] = useState([]);
  const [desc, setDesc] = useState("");
  const [code, setCode] = useState("");
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (open) {
      setSel(null); setDesc(""); setCode("");
      api.get("/ujms/sessions/calendars").then((r) => setCals(r.data.calendars)).catch(() => setCals([]));
    }
  }, [open]);
  const rows = [{ courtroom_id: "", description: "All Courtrooms (search every calendar)", code: "ALL" },
    ...cals.filter((c) => (!desc || c.description.toLowerCase().includes(desc.toLowerCase())) &&
      (!code || c.code.toLowerCase().includes(code.toLowerCase())))];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0" data-testid="select-calendar-dialog">
        <DialogTitle className="sr-only">Select Calendar</DialogTitle>
        <div className="px-4 pt-3 pb-2">
          <div className="text-[15px] text-ujms-dark">Select Calendar</div>
          <div className="flex items-center gap-2 mt-2 text-[12px] text-slate-600">
            Description <input className={cls(inputCls, "w-48 h-7 text-[12px]")} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="cal-desc-filter" />
            Code <input className={cls(inputCls, "w-28 h-7 text-[12px]")} value={code} onChange={(e) => setCode(e.target.value)} data-testid="cal-code-filter" />
          </div>
        </div>
        <div className="mx-4 border border-[#9db8d2]">
          <div className="grid grid-cols-[1fr_120px] tyler-th text-[11px] px-2 py-1"><span>Description</span><span>Code</span></div>
          <div className="max-h-[260px] overflow-y-auto bg-white">
            {rows.map((c, i) => (
              <button key={c.code + i} onClick={() => setSel(c)} data-testid={`cal-row-${c.code}`}
                className={cls("w-full text-left grid grid-cols-[1fr_120px] px-2 py-1 text-[12px]",
                  sel?.code === c.code ? "bg-ujms-dark text-white" : i % 2 ? "bg-ujms-soft hover:bg-ujms-tint" : "bg-white hover:bg-ujms-tint")}>
                <span>{c.description}</span><span className="font-mono">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-1.5 px-4 py-3">
          <button className="ody2-btn-gray" onClick={onClose} data-testid="cal-cancel">Cancel</button>
          <button className="ody2-btn-blue" disabled={!sel} data-testid="cal-continue"
            onClick={() => { onSelect(sel); onClose(); }}>Continue</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Select Time (slot picker within chosen sessions) ----------------
function SelectTimeDialog({ open, onClose, sessions, duration, onDone }) {
  const [slotMap, setSlotMap] = useState({});
  const [chosen, setChosen] = useState({});
  useEffect(() => {
    if (open && sessions.length) {
      setChosen({});
      sessions.forEach((s) => {
        api.get("/ujms/sessions/slots", { params: { date: s.date, courtroom_id: s.courtroom_id, start: s.start_time, end: s.end_time, duration } })
          .then((r) => {
            setSlotMap((m) => ({ ...m, [s.key]: r.data.slots }));
            const first = r.data.slots.find((x) => x.available);
            if (first) setChosen((c0) => ({ ...c0, [s.key]: first.time }));
          }).catch(() => {});
      });
    }
  }, [open, sessions, duration]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0" data-testid="select-time-dialog">
        <DialogTitle className="sr-only">Select Time</DialogTitle>
        <div className="px-4 pt-3 pb-1 text-[15px] text-ujms-dark">Select Time</div>
        <div className="px-4 pb-2 text-[11px] text-slate-500">Pick an available start time inside each selected session ({duration} min settings).</div>
        <div className="mx-4 mb-2 border border-[#9db8d2] max-h-[300px] overflow-y-auto bg-white">
          {sessions.map((s) => (
            <div key={s.key} className="border-b border-[#dbe6f1] last:border-0 px-2 py-2" data-testid={`time-session-${s.key.replace(/[|:]/g, "-")}`}>
              <div className="text-[12px] font-bold text-slate-800">
                {new Date(s.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                <span className="font-normal text-slate-500 ml-2">{s.courtroom} · {s.start_time}–{s.end_time}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {(slotMap[s.key] || []).map((sl) => (
                  <button key={sl.time} disabled={!sl.available}
                    onClick={() => setChosen((c0) => ({ ...c0, [s.key]: sl.time }))}
                    data-testid={`slot-${s.key.replace(/[|:]/g, "-")}-${sl.time.replace(":", "")}`}
                    className={cls("px-2 py-0.5 text-[11.5px] border rounded-[2px] font-mono",
                      chosen[s.key] === sl.time ? "bg-ujms-dark text-white border-ujms-dark"
                        : sl.available ? "bg-ujms-soft border-[#9db8d2] hover:bg-ujms-tint" : "bg-slate-100 text-slate-300 border-slate-200 line-through")}>
                    {sl.time}
                  </button>
                ))}
                {!(slotMap[s.key] || []).length && <span className="text-[11px] text-slate-400">Loading slots…</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-1.5 px-4 py-3">
          <button className="ody2-btn-gray" onClick={onClose} data-testid="time-cancel">Cancel</button>
          <button className="ody2-btn-blue" data-testid="time-continue"
            onClick={() => { onDone(sessions.map((s) => ({ ...s, chosen_time: chosen[s.key] || s.start_time }))); onClose(); }}>Continue</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Select Events (tie setting to case events) ----------------
function SelectEventsDialog({ open, onClose, filings, initial, onDone }) {
  const [q, setQ] = useState("");
  const [checked, setChecked] = useState([]);
  useEffect(() => { if (open) { setChecked(initial || []); setQ(""); } }, [open, initial]);
  const rows = filings.filter((f) => !q || `${f.filing_type} ${f.filing_title || ""}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setChecked((c0) => c0.includes(id) ? c0.filter((x) => x !== id) : [...c0, id]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0" data-testid="select-events-dialog">
        <DialogTitle className="sr-only">Select Events</DialogTitle>
        <div className="px-4 pt-3 pb-2">
          <div className="text-[15px] text-ujms-dark">Select Events</div>
          <div className="flex items-center gap-2 mt-2 text-[12px] text-slate-600">
            Search <input className={cls(inputCls, "w-64 h-7 text-[12px]")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="ev-search" />
            <button className="ody2-btn-gray ml-auto" onClick={() => setChecked(rows.map((f) => f.id))} data-testid="ev-select-all">Select All</button>
          </div>
        </div>
        <div className="mx-4 border border-[#9db8d2]">
          <div className="grid grid-cols-[30px_90px_1fr] tyler-th text-[11px] px-2 py-1"><span></span><span>Date</span><span>Type &amp; Comments</span></div>
          <div className="max-h-[250px] overflow-y-auto bg-white">
            {rows.map((f, i) => (
              <label key={f.id} data-testid={`ev-row-${f.id}`}
                className={cls("grid grid-cols-[30px_90px_1fr] px-2 py-1 text-[12px] cursor-pointer items-center",
                  i % 2 ? "bg-ujms-soft" : "bg-white", "hover:bg-ujms-tint")}>
                <input type="checkbox" checked={checked.includes(f.id)} onChange={() => toggle(f.id)} />
                <span className="font-mono text-[11px]">{(f.created_at || "").slice(0, 10)}</span>
                <span><b>{f.filing_type}</b>{f.filing_title ? ` — ${f.filing_title}` : ""}</span>
              </label>
            ))}
            {!rows.length && <div className="p-3 text-[12px] text-slate-400">No case events match.</div>}
          </div>
        </div>
        <div className="flex justify-end gap-1.5 px-4 py-3">
          <button className="ody2-btn-gray" onClick={onClose} data-testid="ev-cancel">Cancel</button>
          <button className="ody2-btn-blue" data-testid="ev-continue" onClick={() => { onDone(checked); onClose(); }}>Continue</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Find Court Sessions ----------------
export function FindSessionsDialog({ open, onClose, caseId, c, cfg, filings = [], onDone, initialType }) {
  const [form, setForm] = useState({ hearing_type: "MOTION_HEARING", courtroom_id: "", date_from: "", date_to: "" });
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState([]);
  const [duration, setDuration] = useState(60);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveStage, setSaveStage] = useState("");
  const [calOpen, setCalOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [evOpen, setEvOpen] = useState(false);
  const [calLabel, setCalLabel] = useState("");
  const [timeFilter, setTimeFilter] = useState({ from: "", to: "" });
  const [chosenEvents, setChosenEvents] = useState([]);
  const [interpReq, setInterpReq] = useState(false);
  const [interpLang, setInterpLang] = useState("Spanish");
  const [languages, setLanguages] = useState([]);
  useEffect(() => {
    if (open) {
      const d0 = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
      const d1 = new Date(Date.now() + 8 * 864e5).toISOString().slice(0, 10);
      setForm({ hearing_type: initialType || (cfg?.hearing_types?.[0]?.code) || "MOTION_HEARING", courtroom_id: c?.current_courtroom_id || "", date_from: d0, date_to: d1 });
      setSessions(null); setSelected([]); setComment("");
      setCalLabel(c?.current_courtroom_name || ""); setTimeFilter({ from: "", to: "" }); setChosenEvents([]); setSaveStage("");
      setInterpReq(false);
      api.get("/ujms/interpreters/catalog").then((r) => setLanguages(r.data.languages || [])).catch(() => setLanguages([]));
    }
  }, [open, c, cfg, initialType]);
  const find = async () => {
    try {
      const r = await ujmsFindSessions({ ...form, case_id: caseId, courtroom_id: form.courtroom_id || undefined,
        time_from: timeFilter.from || undefined, time_to: timeFilter.to || undefined });
      setSessions(r.data.sessions);
      if (!r.data.sessions.length) toast.info("No available sessions in this window");
    } catch (e) { err(e, "Session search failed"); }
  };
  const toggle = (s) => {
    const key = `${s.date}|${s.start_time}|${s.courtroom_id}`;
    setSelected((sel) => sel.some((x) => x.key === key) ? sel.filter((x) => x.key !== key) : [...sel, { ...s, key }]);
  };
  const save = async () => {
    if (!selected.length) { toast.error("Click a row in the Session Search Results table"); return; }
    setBusy(true);
    setSaveStage("Checking for Conflicts…");
    const evLabels = chosenEvents.map((id) => {
      const f = filings.find((x) => x.id === id);
      return f ? (f.filing_title || f.filing_type) : null;
    }).filter(Boolean);
    const notes = [comment.trim(), evLabels.length ? `Set on: ${evLabels.join("; ")}` : ""].filter(Boolean).join(" · ") || undefined;
    const bookInterpreter = async (hid) => {
      if (!interpReq || !hid) return;
      try {
        await api.post(`/ujms/hearings/${hid}/interpreter-request`, {
          language: interpLang, note: "Requested at scheduling (Find Court Sessions)" });
      } catch (e) { err(e, "Hearing saved, but the interpreter request failed"); }
    };
    let ok = 0;
    for (const s of selected) {
      setSaveStage("Saving Your Hearing…");
      const payload = { hearing_type: form.hearing_type, date: s.date, start_time: s.chosen_time || s.start_time,
        duration_minutes: duration, courtroom_id: s.courtroom_id,
        judge_id: c?.current_judge_id || undefined, notes };
      try {
        const r = await ujmsScheduleHearing(caseId, payload);
        await bookInterpreter(r.data?.id);
        ok += 1;
      } catch (e) {
        if (e.response?.status === 409) {
          try {
            const r2 = await ujmsScheduleHearing(caseId, { ...payload, override: true, override_reason: "Session selected from Find Court Sessions" });
            await bookInterpreter(r2.data?.id);
            ok += 1;
          } catch (e2) { err(e2, `Could not schedule ${s.date}`); }
        } else { err(e, `Could not schedule ${s.date}`); }
      }
    }
    setBusy(false);
    setSaveStage("");
    if (ok) { toast.success(`${ok} hearing session(s) scheduled`); onDone?.(); onClose(); }
  };

  const [mode, setMode] = useState("find");
  useEffect(() => { if (open) setMode("find"); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0 max-h-[90vh] overflow-y-auto" data-testid="find-sessions-dialog">
        <DialogTitle className="sr-only">Schedule Hearing</DialogTitle>
        <div className="flex items-center gap-3 px-4 pt-3" data-testid="schedule-hearing-modes">
          <div className="text-[15px] text-ujms-dark font-semibold">Schedule Hearing</div>
          <Seg options={[["find", "Find court session"], ["manual", "Set date & time"]]} value={mode} onChange={setMode} testId="schedule-mode" />
        </div>
        {mode === "manual" && cfg && <div className="px-4 pt-3 pb-4" data-testid="schedule-manual"><ManualHearingForm caseId={caseId} cfg={cfg} c={c} onDone={onDone} onClose={onClose} initialType={initialType} /></div>}
        <div className={cls("px-4 pt-2", mode !== "find" && "hidden")}>
          <div className="text-[12px] text-slate-500">Search open sessions for this case's courtroom and pick one or more blocks.</div>
          <div className="text-right -mt-4">
            <div className="text-[13px] font-bold text-slate-900">{c?.caption}</div>
            <div className="text-[11px] text-slate-500">Case # <b>{c?.case_number}</b> · Type {c?.case_type_label || c?.case_type}</div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2">
            <div className="space-y-2">
              <label className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600">Type
                <select className={cls(selectCls, "h-7 text-[12px] py-0")} value={form.hearing_type}
                  onChange={(e) => setForm({ ...form, hearing_type: e.target.value })} data-testid="fs-type">
                  {(cfg?.hearing_types || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </label>
              <label className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600">Location
                <div className="flex gap-1">
                  <input readOnly className={cls(inputCls, "h-7 text-[12px] flex-1 bg-[#f5f8fb] cursor-pointer")}
                    value={calLabel || "All courtrooms"} onClick={() => setCalOpen(true)} data-testid="fs-room" />
                  <button type="button" className="ody2-btn-gray px-2.5" onClick={() => setCalOpen(true)} data-testid="fs-room-picker">…</button>
                </div>
              </label>
              <label className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600">Comment
                <input className={cls(inputCls, "h-7 text-[12px]")} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="fs-comment" />
              </label>
            </div>
            <div className="space-y-2">
              <label className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600">From
                <input type="date" className={cls(inputCls, "h-7 text-[12px]")} value={form.date_from}
                  onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="fs-from" />
              </label>
              <label className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600">To
                <input type="date" className={cls(inputCls, "h-7 text-[12px]")} value={form.date_to}
                  onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="fs-to" />
              </label>
              <div className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600">Time
                <div className="flex gap-1 items-center">
                  <input type="time" className={cls(inputCls, "h-7 text-[12px] w-[100px]")} value={timeFilter.from}
                    onChange={(e) => setTimeFilter({ ...timeFilter, from: e.target.value })} data-testid="fs-time-from" />
                  <span className="text-slate-400">–</span>
                  <input type="time" className={cls(inputCls, "h-7 text-[12px] w-[100px]")} value={timeFilter.to}
                    onChange={(e) => setTimeFilter({ ...timeFilter, to: e.target.value })} data-testid="fs-time-to" />
                </div>
              </div>
              <div className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600">Dur (min)
                <div className="flex gap-2">
                  <input type="number" className={cls(inputCls, "h-7 text-[12px] w-20")} value={duration}
                    onChange={(e) => setDuration(+e.target.value || 30)} data-testid="fs-duration" />
                  <button className={cls(btnPrimary, "h-7 py-0 px-5 text-[12px]")} onClick={find} data-testid="fs-find-btn">Find</button>
                </div>
              </div>
              <div className="grid grid-cols-[70px_1fr] items-center text-[12px] text-slate-600"><span />
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input type="checkbox" checked={interpReq} onChange={(e) => setInterpReq(e.target.checked)}
                      data-testid="fs-interpreter-required" /> Interpreter Required
                  </label>
                  {interpReq && (
                    <select className={cls(selectCls, "h-7 text-[12px] py-0 w-36")} value={interpLang}
                      onChange={(e) => setInterpLang(e.target.value)} data-testid="fs-interpreter-language">
                      {languages.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={cls("grid grid-cols-[1fr_240px] gap-3 px-4 mt-3", mode !== "find" && "hidden")}>
          <div>
            <div className="tyler-th text-[11px] px-2 py-1">Session Search Results</div>
            <div className="border border-[#9db8d2] border-t-0 max-h-[220px] overflow-y-auto" data-testid="fs-results">
              {(sessions || []).map((s, i) => {
                const key = `${s.date}|${s.start_time}|${s.courtroom_id}`;
                const sel = selected.some((x) => x.key === key);
                return (
                  <button key={key} onClick={() => toggle(s)} data-testid={`fs-session-${s.date}-${s.block}-${i}`}
                    className={cls("w-full text-left grid grid-cols-[46px_86px_120px_1fr] px-2 py-1 text-[12px]",
                      sel ? "bg-ujms-dark text-white" : i % 2 ? "bg-ujms-soft hover:bg-ujms-tint" : "bg-white hover:bg-ujms-tint")}>
                    <span className={cls("font-mono", !sel && (s.utilization > 66 ? "text-red-700" : s.utilization > 33 ? "text-amber-700" : "text-emerald-700"))}>{s.utilization}%</span>
                    <span className="font-mono">{s.date.slice(5)}</span>
                    <span>{s.start_time} - {s.end_time}</span>
                    <span className="truncate">{s.label} · {s.courtroom}</span>
                  </button>
                );
              })}
              {sessions === null && <div className="p-3 text-[12px] text-slate-400">Set the criteria above and click <b>Find</b>.</div>}
              {sessions?.length === 0 && <div className="p-3 text-[12px] text-slate-400">No available sessions match.</div>}
            </div>
          </div>
          <div>
            <div className="tyler-th text-[11px] px-2 py-1">Selected Sessions</div>
            <div className="border border-[#9db8d2] border-t-0 h-[220px] overflow-y-auto p-1.5" data-testid="fs-selected">
              {selected.length === 0 ? (
                <div className="text-[11px] text-slate-400 text-center mt-14 px-2">Click on a row in the Session Search Results table.</div>
              ) : selected.map((s) => (
                <div key={s.key} className="text-[11px] bg-ujms-soft border border-[#bcd4ea] rounded-[2px] px-1.5 py-1 mb-1 flex justify-between">
                  <span className="font-mono">{s.date} {s.chosen_time || s.start_time}</span>
                  <button className="text-red-600 font-bold" onClick={() => toggle(s)} data-testid={`fs-unselect-${s.key.replace(/[|:]/g, "-")}`}>×</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={cls("px-4 mt-3", mode !== "find" && "hidden")}>
          <div className="tyler-th text-[11px] px-2 py-1">Supplemental Hearing Information</div>
          <div className="border border-[#9db8d2] border-t-0 px-2 py-2 flex items-center gap-2 flex-wrap text-[12px] text-slate-600">
            Events
            <button type="button" className="ody2-btn-gray" onClick={() => setEvOpen(true)} data-testid="fs-select-events">Select Events…</button>
            {chosenEvents.length === 0 && <span className="text-slate-400">Tie this setting to one or more case events (optional).</span>}
            {chosenEvents.map((id) => {
              const f = filings.find((x) => x.id === id);
              return f ? (
                <span key={id} className="bg-ujms-soft border border-[#bcd4ea] rounded-[2px] px-1.5 py-0.5 text-[11px]" data-testid={`fs-event-chip-${id}`}>
                  {(f.created_at || "").slice(0, 10)} {f.filing_type}
                  <button className="ml-1 text-red-600 font-bold" onClick={() => setChosenEvents((c0) => c0.filter((x) => x !== id))}>×</button>
                </span>
              ) : null;
            })}
          </div>
        </div>
        <DialogFooter className={cls("px-4 py-3", mode !== "find" && "hidden")}>
          <button className="ody2-btn-gray" onClick={onClose} data-testid="fs-exit">Exit</button>
          <button className="ody2-btn-gray" disabled={!selected.length} onClick={() => setTimeOpen(true)} data-testid="fs-select-time">Select Time…</button>
          <button className="ody2-btn-blue" disabled={busy || !selected.length} onClick={save} data-testid="fs-save">
            {busy ? saveStage || "Scheduling…" : `Save (${selected.length})`}
          </button>
        </DialogFooter>
        <SelectCalendarDialog open={calOpen} onClose={() => setCalOpen(false)}
          onSelect={(cal) => { setForm((f) => ({ ...f, courtroom_id: cal.courtroom_id })); setCalLabel(cal.courtroom_id ? cal.description : ""); }} />
        <SelectTimeDialog open={timeOpen} onClose={() => setTimeOpen(false)} sessions={selected} duration={duration}
          onDone={(upd) => setSelected(upd)} />
        <SelectEventsDialog open={evOpen} onClose={() => setEvOpen(false)} filings={filings} initial={chosenEvents}
          onDone={(ids) => setChosenEvents(ids)} />
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Odyssey right rail ----------------
export function OdysseyRail({ c, openTasks, setTab, onForms, recent = [] }) {
  const navigate = useNavigate();
  const [sel, setSel] = useState("");
  const favKey = "ujms_fav_cases";
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem(favKey) || "[]"); } catch { return []; } });
  const isFav = favs.some((f) => f.id === c.id);
  const toggleFav = () => {
    const next = isFav ? favs.filter((f) => f.id !== c.id) : [...favs, { id: c.id, case_number: c.case_number }].slice(-10);
    setFavs(next); localStorage.setItem(favKey, JSON.stringify(next));
    toast.success(isFav ? "Removed from Favorites" : "Added to Favorites");
  };
  const go = () => {
    if (sel.trim()) navigate(`/ujms/cases?q=${encodeURIComponent(sel.trim())}`);
  };
  const item = "flex items-center gap-1.5 text-[12.5px] text-slate-600 hover:text-ujms-dark py-[3px] w-full text-left";
  return (
    <aside className="w-[185px] shrink-0 hidden xl:block text-left pt-1" data-testid="odyssey-rail">
      <button className={item} onClick={toggleFav} data-testid="rail-favorites">
        <Star className={cls("w-3.5 h-3.5", isFav && "fill-amber-400 text-amber-500")} /> Favorites{isFav ? " ★" : ""}
      </button>
      <button className={item} onClick={() => setTab("Tasks")} data-testid="rail-tasks">
        <ListChecks className="w-3.5 h-3.5" /> Tasks{openTasks ? ` (${openTasks})` : ""}
      </button>
      <button className={item} onClick={() => navigate("/ujms/tasks")} data-testid="rail-jobs"><Inbox className="w-3.5 h-3.5" /> Jobs</button>
      <button className={item} onClick={() => navigate("/ujms/digest")} data-testid="rail-messages"><Mail className="w-3.5 h-3.5" /> Messages</button>
      <div className="mt-4 text-[12px] font-semibold text-slate-500" data-testid="rail-whats-new">What's New</div>
      <div className="mt-1 space-y-0.5">
        {recent.slice(0, 5).map((rc) => (
          <button key={rc.id} onClick={() => navigate(`/ujms/cases/${rc.id}`)} data-testid={`rail-recent-${rc.id}`}
            className={cls("block w-full text-left text-[12px] font-mono px-1.5 py-0.5 border-l-2",
              rc.id === c.id ? "border-ujms-dark text-ujms-dark bg-ujms-soft font-bold" : "border-transparent text-slate-500 hover:text-ujms-dark")}>
            {rc.case_number}
          </button>
        ))}
        <div className="text-[12px] text-slate-400 px-1.5">Court Session</div>
      </div>
      <div className="mt-6 border-t border-slate-200 pt-3 space-y-0.5">
        <button className={cls(item, "font-semibold text-slate-700")} onClick={() => setTab("Tasks")} data-testid="rail-create-task">
          <span className="text-[10px] border border-slate-400 px-0.5">CT</span> Create Task
        </button>
        <button className={item} onClick={() => setTab("Orders")} data-testid="rail-esigs"><PenLine className="w-3.5 h-3.5" /> ESigs</button>
        <button className={item} onClick={onForms} data-testid="rail-forms"><FileStack className="w-3.5 h-3.5" /> Forms</button>
        <span className={cls(item, "text-slate-300 cursor-default")}><Save className="w-3.5 h-3.5" /> Save/Copy</span>
      </div>
      <input className="mt-4 w-full border border-slate-300 rounded-[2px] px-2 py-1 text-[12px] bg-white"
        placeholder="Enter selection…" value={sel} onChange={(e) => setSel(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && go()} data-testid="rail-enter-selection" />
    </aside>
  );
}
